import type {
  AggregateOffer,
  BreadcrumbList,
  FAQPage,
  Offer,
  Place,
  Product,
  Thing,
  WithContext
} from 'schema-dts';

// schema.org JSON-LD nodes for a product page (Product / BreadcrumbList /
// FAQPage), strongly typed against schema.org via `schema-dts` so typos and
// invalid enum values fail at compile time. Rendered by the view with
// react-schemaorg's <JsonLd>. `compact` drops undefined/null/'' at runtime so
// we only emit fields we actually have — never empty or fake values (no price
// -> no offers, no coordinates -> no geo).

const compact = <T>(obj: T): T =>
  Object.fromEntries(
    Object.entries(obj as Record<string, unknown>).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  ) as T;

export interface ProductPageSeoInput {
  siteName: string;
  pageUrl: string;
  product: {
    name: string;
    description?: string;
    sku?: string;
    category?: string;
    images?: string[];
  };
  offer?: {
    // Lowest unit price. When highPrice differs, an AggregateOffer price range
    // is emitted instead of a single Offer.
    price?: number;
    highPrice?: number;
    offerCount?: number;
    currency?: string;
    inStock?: boolean;
  };
  place?: { name?: string; address?: string; lat?: number; lon?: number };
  breadcrumbs?: { name: string; url: string }[];
  faqs?: { question: string; answer: string }[];
}

const buildOffer = (
  offer: ProductPageSeoInput['offer'],
  url: string,
  sellerName: string,
  place: ProductPageSeoInput['place']
): Offer | AggregateOffer | undefined => {
  if (!offer || offer.price == null || offer.price <= 0) {
    return undefined;
  }

  const hasPlace =
    !!place &&
    (place.name != null ||
      place.address != null ||
      place.lat != null ||
      place.lon != null);

  const baseOffer = compact<Offer>({
    '@type': 'Offer',
    price: offer.price,
    priceCurrency: offer.currency,
    availability:
      offer.inStock === false
        ? 'https://schema.org/OutOfStock'
        : 'https://schema.org/InStock',
    url,
    seller: { '@type': 'Organization', name: sellerName },
    availableAtOrFrom: hasPlace
      ? compact<Place>({
          '@type': 'Place',
          name: place!.name,
          address: place!.address,
          geo:
            place!.lat != null && place!.lon != null
              ? {
                  '@type': 'GeoCoordinates',
                  latitude: place!.lat,
                  longitude: place!.lon
                }
              : undefined
        })
      : undefined
  });

  // Multiple unit prices -> AggregateOffer expressing the low/high range.
  if (offer.highPrice != null && offer.highPrice > offer.price) {
    return compact<AggregateOffer>({
      '@type': 'AggregateOffer',
      lowPrice: offer.price,
      highPrice: offer.highPrice,
      priceCurrency: offer.currency,
      offerCount: offer.offerCount,
      offers: baseOffer
    });
  }
  return baseOffer;
};

export const productPageJsonLd = (
  input: ProductPageSeoInput
): WithContext<Thing>[] => {
  const { siteName, pageUrl, product, offer, place, breadcrumbs, faqs } = input;
  const nodes: WithContext<Thing>[] = [];

  nodes.push(
    compact<WithContext<Product>>({
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.name,
      description: product.description,
      sku: product.sku,
      category: product.category,
      url: pageUrl,
      image: product.images?.length ? product.images : undefined,
      brand: { '@type': 'Brand', name: siteName },
      offers: buildOffer(offer, pageUrl, siteName, place)
    })
  );

  if (breadcrumbs?.length) {
    nodes.push({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.name,
        item: { '@id': crumb.url }
      }))
    } satisfies WithContext<BreadcrumbList>);
  }

  const validFaqs = (faqs ?? [])
    .map(faq => ({ question: faq.question.trim(), answer: faq.answer.trim() }))
    .filter(faq => faq.question && faq.answer);

  if (validFaqs.length) {
    nodes.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: validFaqs.map(faq => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: { '@type': 'Answer', text: faq.answer }
      }))
    } satisfies WithContext<FAQPage>);
  }

  return nodes;
};
